import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2, Heart, TrendingUp, Activity, Crown, Settings, Sparkles, CreditCard, CalendarHeart, ShieldCheck, Leaf } from "lucide-react";
import { differenceInDays, format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { PrivacyDataCard } from "@/components/PrivacyDataCard";
import { MFACard } from "@/components/MFACard";

function getSeasonalNote(symptoms: string[], goals: string[]) {
  const hasSleep = symptoms.some(s => s.toLowerCase().includes("sleep"));
  const hasHotFlashes = symptoms.some(s => s.toLowerCase().includes("hot"));
  const hasMood = symptoms.some(s => s.toLowerCase().includes("mood") || s.toLowerCase().includes("anxiety"));
  const hasFog = symptoms.some(s => s.toLowerCase().includes("fog"));
  const wantsSleep = goals.some(g => g.toLowerCase().includes("sleep"));
  const wantsMood = goals.some(g => g.toLowerCase().includes("mood") || g.toLowerCase().includes("anxiety"));

  if (hasSleep || wantsSleep) {
    return "Focus on calming evening rituals this week. A cool, dark room and a consistent wind-down can make a real difference to sleep quality.";
  }
  if (hasHotFlashes) {
    return "Layered clothing, cool drinks, and stress-busting breathwork can help you ride the heat with more ease.";
  }
  if (hasMood || wantsMood) {
    return "Small moments of movement, sunlight, and connection can gently steady the emotional waves.";
  }
  if (hasFog) {
    return "Prioritize one task at a time, stay hydrated, and protect your sleep — clarity often follows rest.";
  }
  return "Your journey is unique. Keep logging what you notice — patterns will reveal themselves over time.";
}

function getSeasonalTopic(knowledgeLevel: string | null, symptoms: string[]) {
  if (knowledgeLevel === "expert") return "Advanced symptom patterns and hormonal interplay";
  if (knowledgeLevel === "intermediate") return "The science of cortisol, estrogen, and sleep";
  const hasSleep = symptoms.some(s => s.toLowerCase().includes("sleep"));
  if (hasSleep) return "The basics of sleep and your changing cycle";
  return "The foundations of your menopause journey";
}

export default function Profile() {
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { subscribed, isTrialing, isFreeExploring, freeAccessEndsAt, subscriptionEnd, openCustomerPortal } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [journeyStage, setJourneyStage] = useState<string | null>(null);
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const [knowledgeLevel, setKnowledgeLevel] = useState<string | null>(null);
  const [postCount, setPostCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to open subscription management. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (error) throw error;

      if (data) {
        setDisplayName(data.display_name || "");
        setJourneyStage(data.journey_stage);
        setSymptoms(data.symptoms || []);
        setGoals(data.goals || []);
        setKnowledgeLevel(data.knowledge_level);
        setPostCount(data.post_count || 0);
        setCommentCount(data.comment_count || 0);
        setAvatarUrl(data.avatar_url || null);
      }
    } catch (error: any) {
      toast({
        title: "Error loading profile",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back Home
        </Button>

        {/* Subscription — Editorial Bloom */}
        {(() => {
          const stage: "free" | "trial" | "premium" | "none" = isFreeExploring
            ? "free"
            : isTrialing
            ? "trial"
            : subscribed
            ? "premium"
            : "none";

          const endRef = isFreeExploring ? freeAccessEndsAt : subscriptionEnd;
          const daysLeft = endRef
            ? Math.max(0, differenceInDays(new Date(endRef), new Date()))
            : null;

          const heading =
            stage === "free"
              ? "You're exploring Her Season"
              : stage === "trial"
              ? "Your free trial is blooming"
              : stage === "premium"
              ? "You're a Her Season member"
              : "Begin your Her Season";

          const subheading =
            stage === "free"
              ? "Five gentle days to wander. No card, no pressure."
              : stage === "trial"
              ? "Fourteen days of full access. Cancel anytime — no charge before day 14."
              : stage === "premium"
              ? "Thank you for being here. Everything is unlocked, always."
              : "Start with 5 free days, then a 14-day trial. Cancel anytime.";

          const benefits = [
            { icon: Sparkles, text: "Unlimited chats with Aura, your gentle guide" },
            { icon: CalendarHeart, text: "Full symptom, mood & cycle tracking" },
            { icon: Leaf, text: "Every article, ritual and guided practice" },
            { icon: ShieldCheck, text: "Private by design. Your data stays yours." },
          ];

          return (
            <Card className="mb-8 overflow-hidden border-primary/15 shadow-sm">
              <div className="relative bg-gradient-to-br from-[hsl(28_60%_96%)] via-[hsl(15_55%_94%)] to-[hsl(340_45%_93%)] px-6 py-8 sm:px-10 sm:py-10">
                <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl" aria-hidden />
                <div className="absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-accent/10 blur-3xl" aria-hidden />

                <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                  <div className="max-w-lg">
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-background/70 px-3 py-1 text-xs font-medium tracking-wide text-primary backdrop-blur">
                      <Crown className="h-3.5 w-3.5" />
                      {stage === "free"
                        ? "Free exploration"
                        : stage === "trial"
                        ? "Trial in progress"
                        : stage === "premium"
                        ? "Premium member"
                        : "Get started"}
                    </div>
                    <h2 className="font-serif text-3xl leading-tight text-foreground sm:text-4xl">
                      {heading}
                    </h2>
                    <p className="mt-2 text-base text-muted-foreground">
                      {subheading}
                    </p>

                    {daysLeft !== null && stage !== "premium" && (
                      <div className="mt-5 inline-flex items-baseline gap-2 rounded-2xl bg-background/70 px-4 py-3 backdrop-blur">
                        <span className="font-serif text-4xl text-primary">{daysLeft}</span>
                        <span className="text-sm text-muted-foreground">
                          {daysLeft === 1 ? "day" : "days"} left
                          {endRef && (
                            <> · ends {format(new Date(endRef), "MMM d")}</>
                          )}
                        </span>
                      </div>
                    )}

                    {stage === "premium" && subscriptionEnd && (
                      <p className="mt-5 text-sm text-muted-foreground">
                        Next renewal: <span className="font-medium text-foreground">{format(new Date(subscriptionEnd), "MMMM d, yyyy")}</span>
                      </p>
                    )}
                  </div>

                  <div className="hidden sm:block">
                    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-background/60 backdrop-blur">
                      <Heart className="h-10 w-10 text-primary" fill="currentColor" fillOpacity={0.15} />
                    </div>
                  </div>
                </div>

                <div className="relative mt-8 grid gap-3 sm:grid-cols-2">
                  {benefits.map(({ icon: Icon, text }) => (
                    <div key={text} className="flex items-start gap-3 rounded-xl bg-background/50 px-3 py-2.5 backdrop-blur">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span className="text-sm text-foreground/80">{text}</span>
                    </div>
                  ))}
                </div>

                <div className="relative mt-8 flex flex-col gap-2 sm:flex-row sm:items-center">
                  {stage === "premium" ? (
                    <>
                      <Button
                        onClick={handleManageSubscription}
                        className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <CreditCard className="h-4 w-4" />
                        Manage billing details
                      </Button>
                      <Button variant="ghost" onClick={handleManageSubscription} className="gap-2 text-muted-foreground hover:text-foreground">
                        Cancel subscription
                      </Button>
                    </>
                  ) : stage === "trial" ? (
                    <>
                      <Button
                        onClick={() => navigate("/pricing")}
                        className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <Crown className="h-4 w-4" />
                        Continue to premium
                      </Button>
                      <Button variant="ghost" onClick={handleManageSubscription} className="gap-2 text-muted-foreground hover:text-foreground">
                        Manage or cancel trial
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => navigate("/pricing")}
                      className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      <Sparkles className="h-4 w-4" />
                      {stage === "free" ? "Start 14-day free trial" : "Begin your journey"}
                    </Button>
                  )}
                </div>

                <p className="relative mt-4 text-xs text-muted-foreground">
                  Billing is handled securely by Stripe. You'll only visit Stripe when you choose to update your card or cancel.
                </p>
              </div>
            </Card>
          );
        })()}

        {/* Personal Archive — Editorial Spread */}
        <Card className="mb-6 overflow-hidden border-border/60 shadow-soft bg-background">
          <div className="relative p-8 md:p-10">
            {/* Decorative background element */}
            <div className="absolute -top-12 -right-12 w-48 h-48 border border-primary/10 rounded-full" aria-hidden />

            <div className="relative">
              {/* Header */}
              <header className="mb-8 md:mb-12 border-b border-primary/20 pb-6 md:pb-8">
                <div className="flex flex-col md:flex-row justify-between md:items-end gap-6">
                  <div className="flex items-end gap-4">
                    <Avatar className="h-16 w-16 md:h-20 md:w-20 border-2 border-primary/20 shadow-soft">
                      <AvatarImage src={avatarUrl || undefined} />
                      <AvatarFallback className="text-2xl md:text-3xl bg-primary/10 text-primary">
                        {displayName.charAt(0).toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-primary uppercase tracking-[0.2em] text-[10px] font-bold mb-1">
                        Personal Archive
                      </p>
                      <h1 className="text-foreground text-3xl md:text-5xl font-light italic leading-tight font-serif">
                        {displayName || "Your Profile"}
                      </h1>
                    </div>
                  </div>
                  <div className="text-left md:text-right">
                    <p className="text-accent text-lg italic font-serif">
                      {journeyStage
                        ? journeyStage.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
                        : "Exploring"}
                    </p>
                    <p className="text-primary text-[11px] uppercase tracking-widest mt-1">
                      Journey Stage
                    </p>
                  </div>
                </div>
              </header>

              {/* Stats row */}
              <div className="flex justify-center gap-8 md:gap-12 mb-10 md:mb-12">
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-serif text-primary">{postCount}</p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Posts</p>
                </div>
                <div className="w-px bg-border" />
                <div className="text-center">
                  <p className="text-3xl md:text-4xl font-serif text-accent">{commentCount}</p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Comments</p>
                </div>
              </div>

              {/* Two-column body */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                {/* Left: Goals & Knowledge */}
                <div className="md:col-span-7 space-y-10">
                  <section>
                    <h3 className="text-foreground text-[12px] uppercase tracking-[0.15em] font-bold mb-4 flex items-center gap-3 font-sans">
                      Current Goals
                      <span className="h-px flex-1 bg-border" />
                    </h3>
                    {goals.length > 0 ? (
                      <ul className="space-y-4">
                        {goals.slice(0, 5).map((goal, i) => (
                          <li key={goal} className="flex items-start gap-4">
                            <span className="text-accent font-serif italic text-xl min-w-[1.75rem]">
                              {String(i + 1).padStart(2, "0")}.
                            </span>
                            <p className="text-muted-foreground text-[15px] leading-relaxed">
                              {goal}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground italic">No goals selected yet.</p>
                    )}
                  </section>

                  <section className="pt-4">
                    <h3 className="text-foreground text-[12px] uppercase tracking-[0.15em] font-bold mb-6 flex items-center gap-3 font-sans">
                      Knowledge Level
                      <span className="h-px flex-1 bg-border" />
                    </h3>
                    <div className="relative">
                      <div className="flex justify-between items-baseline mb-2">
                        <span className="text-foreground text-lg font-serif">
                          {knowledgeLevel
                            ? knowledgeLevel === "beginner"
                              ? "Curious Learner"
                              : knowledgeLevel === "intermediate"
                              ? "Growing Scholar"
                              : "Seasoned Guide"
                            : "Not set"}
                        </span>
                        <span className="text-primary text-xs font-bold tracking-tighter">
                          {knowledgeLevel === "beginner"
                            ? "33%"
                            : knowledgeLevel === "intermediate"
                            ? "66%"
                            : "100%"} complete
                        </span>
                      </div>
                      <div className="h-[2px] w-full bg-border">
                        <div
                          className="h-full bg-accent relative"
                          style={{
                            width: knowledgeLevel === "beginner"
                              ? "33%"
                              : knowledgeLevel === "intermediate"
                              ? "66%"
                              : "100%"
                          }}
                        >
                          <div className="absolute -right-1 -top-1 w-2.5 h-2.5 bg-accent rounded-full border-2 border-background" />
                        </div>
                      </div>
                      <p className="mt-3 text-primary text-[13px] italic font-serif">
                        Currently exploring: {getSeasonalTopic(knowledgeLevel, symptoms)}
                      </p>
                    </div>
                  </section>
                </div>

                {/* Right: Observations */}
                <div className="md:col-span-5 bg-muted/30 p-6 border-l border-primary/10">
                  <h3 className="text-foreground text-[12px] uppercase tracking-[0.15em] font-bold mb-6 font-sans">
                    Observations
                  </h3>
                  {symptoms.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {symptoms.map((symptom) => (
                        <Badge key={symptom} variant="outline" className="px-3 py-1.5 text-[13px] font-normal bg-background/50">
                          {symptom}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic">No symptoms selected yet.</p>
                  )}

                  <div className="mt-10">
                    <div className="p-4 border border-primary/20 bg-primary/5">
                      <p className="text-foreground italic text-sm leading-relaxed font-serif">
                        "{getSeasonalNote(symptoms, goals)}"
                      </p>
                      <p className="text-primary text-[10px] uppercase font-bold mt-2 tracking-widest font-sans">
                        — Seasonal Note
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <footer className="mt-12 flex justify-center">
                <div className="w-12 h-px bg-accent/40" />
              </footer>
            </div>
          </div>
        </Card>

        {/* Edit Profile action */}
        <div className="flex justify-center mb-8">
          <Button
            variant="outline"
            onClick={() => navigate("/edit-profile")}
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            Edit Profile
          </Button>
        </div>

        {/* Quick Links */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Quick Access</CardTitle>
            <CardDescription>
              Navigate to your wellness dashboard features
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              variant="outline"
              onClick={() => navigate("/insights")}
              className="h-auto py-4 justify-start gap-3"
            >
              <TrendingUp className="h-5 w-5 text-primary" />
              <div className="text-left">
                <div className="font-semibold">AI Insights Dashboard</div>
                <div className="text-xs text-muted-foreground">View symptom patterns & recommendations</div>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/tracker")}
              className="h-auto py-4 justify-start gap-3"
            >
              <Activity className="h-5 w-5 text-primary" />
              <div className="text-left">
                <div className="font-semibold">Daily Tracker</div>
                <div className="text-xs text-muted-foreground">Log symptoms and track progress</div>
              </div>
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => navigate("/admin")}
                className="h-auto py-4 justify-start gap-3 border-primary/50"
              >
                <Crown className="h-5 w-5 text-primary" />
                <div className="text-left">
                  <div className="font-semibold">Admin Dashboard</div>
                  <div className="text-xs text-muted-foreground">Manage user roles & permissions</div>
                </div>
              </Button>
            )}
          </CardContent>
        </Card>

        <MFACard />
        <PrivacyDataCard />
      </div>
    </div>
  );
}
